/**
 * Form validation utilities with type-safe error handling.
 *
 * @module utils/validation
 */

/**
 * Discriminated union for validation results.
 * When valid is false, error is guaranteed to exist.
 * This eliminates the need for non-null assertions.
 */
export type ValidationResult = { valid: true } | { valid: false; error: string };

export interface FieldValidation {
	value: string;
	rules: ValidationRule[];
}

export type ValidationRule =
	| { type: 'required'; message?: string }
	| { type: 'minLength'; min: number; message?: string }
	| { type: 'maxLength'; max: number; message?: string }
	| { type: 'email'; message?: string }
	| { type: 'pattern'; pattern: RegExp; message?: string };

/**
 * Validate a single field value against rules
 */
export function validateField(value: string, rules: ValidationRule[]): ValidationResult {
	for (const rule of rules) {
		switch (rule.type) {
			case 'required':
				if (!value || value.trim().length === 0) {
					return { valid: false, error: rule.message || 'This field is required' };
				}
				break;
			case 'minLength':
				if (value.length < rule.min) {
					return {
						valid: false,
						error: rule.message || `Must be at least ${rule.min} characters`
					};
				}
				break;
			case 'maxLength':
				if (value.length > rule.max) {
					return {
						valid: false,
						error: rule.message || `Must be at most ${rule.max} characters`
					};
				}
				break;
			case 'email':
				if (!isValidEmail(value)) {
					return { valid: false, error: rule.message || 'Invalid email address' };
				}
				break;
			case 'pattern':
				if (!rule.pattern.test(value)) {
					return { valid: false, error: rule.message || 'Invalid format' };
				}
				break;
		}
	}
	return { valid: true };
}

/**
 * Validate multiple fields at once
 */
export function validateForm(
	fields: Record<string, FieldValidation>
): Record<string, ValidationResult> {
	const results: Record<string, ValidationResult> = {};
	for (const [name, field] of Object.entries(fields)) {
		results[name] = validateField(field.value, field.rules);
	}
	return results;
}

/**
 * Check if all validation results are valid
 */
export function isFormValid(results: Record<string, ValidationResult>): boolean {
	return Object.values(results).every((r) => r.valid);
}

/**
 * Email validation (stricter than the old single-line regex).
 * Checks: no consecutive dots in domain, valid local/domain parts,
 * TLD at least 2 chars, no leading/trailing dots.
 */
export function isValidEmail(email: string): boolean {
	const emailRegex =
		/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;
	return emailRegex.test(email);
}

/**
 * Common validation rule sets
 */
export const commonRules = {
	name: [
		{ type: 'required' as const },
		{ type: 'minLength' as const, min: 2, message: 'Name must be at least 2 characters' },
		{ type: 'maxLength' as const, max: 200, message: 'Name must be at most 200 characters' }
	],
	email: [
		{ type: 'required' as const },
		{ type: 'email' as const },
		{ type: 'maxLength' as const, max: 254, message: 'Email must be at most 254 characters' }
	],
	message: [
		{ type: 'required' as const },
		{ type: 'minLength' as const, min: 10, message: 'Message must be at least 10 characters' },
		{ type: 'maxLength' as const, max: 10000, message: 'Message must be at most 10,000 characters' }
	]
};

/**
 * Validate a contact form submission (server-side).
 * Returns { valid, errors } where errors is an array of strings.
 *
 * @param data - Unknown data from request body
 * @returns Validation result with boolean and error messages
 */
export function validateContactForm(data: unknown): { valid: boolean; errors: string[] } {
	const errors: string[] = [];

	if (!data || typeof data !== 'object') {
		return { valid: false, errors: ['Invalid request body'] };
	}

	const form = data as Record<string, unknown>;

	// Type narrowing: when valid is false, error is guaranteed to exist
	const nameResult = validateField(String(form.name ?? ''), commonRules.name);
	if (!nameResult.valid) errors.push(nameResult.error);

	const emailResult = validateField(String(form.email ?? ''), commonRules.email);
	if (!emailResult.valid) errors.push(emailResult.error);

	const messageResult = validateField(String(form.message ?? ''), commonRules.message);
	if (!messageResult.valid) errors.push(messageResult.error);

	return { valid: errors.length === 0, errors };
}
